import webSocketMessageHandler from './WebSocketMessageHandler'

class WebSocketService {
  constructor() {
    this.socket = null
    this.userId = null
    this.pingInterval = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 3000 // 3 seconds
    this.isConnecting = false
    this.listeners = {
      open: [],
      message: [],
      close: [],
      error: []
    }
  }

  /**
   * Connect to WebSocket with user ID
   * @param {number} userId - The user ID to connect with
   */
  connect(userId) {
    if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.OPEN)) {
      return
    }

    this.userId = userId
    this.isConnecting = true
    
    const wsUrl = `wss://9b25k9zc60.execute-api.sa-east-1.amazonaws.com/production/?user_id=${userId}&device=DESKTOP`
    
    try {
      this.socket = new WebSocket(wsUrl)
      this.setupEventListeners()
    } catch (error) {
      console.error('WebSocket connection error:', error)
      this.isConnecting = false
    }
  }

  /**
   * Setup WebSocket event listeners
   */
  setupEventListeners() {
    if (!this.socket) return

    this.socket.onopen = (event) => {
      this.isConnecting = false
      this.reconnectAttempts = 0
      this.startPing()
      this.emit('open', event)
    }

    this.socket.onmessage = (event) => {
      // Process message through the message handler
      webSocketMessageHandler.processMessage(event)
      
      // Also emit the raw message event for backward compatibility
      this.emit('message', event)
    }

    this.socket.onclose = (event) => {
      this.isConnecting = false
      this.stopPing()
      this.emit('close', event)
      
      // Attempt to reconnect if not closed intentionally
      if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.attemptReconnect()
      }
    }

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error)
      this.isConnecting = false
      this.emit('error', error)
    }
  }

  /**
   * Start ping interval to keep connection alive (every 7 minutes)
   */
  startPing() {
    this.stopPing() // Clear any existing interval
    
    // Ping every 7 minutes (420000ms)
    this.pingInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping', timestamp: Date.now() })
      }
    }, 420000) // 7 minutes
  }

  /**
   * Stop ping interval
   */
  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
  }

  /**
   * Attempt to reconnect to WebSocket
   */
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return
    }

    this.reconnectAttempts++
    
    setTimeout(() => {
      if (this.userId) {
        this.connect(this.userId)
      }
    }, this.reconnectDelay * this.reconnectAttempts)
  }

  /**
   * Send message through WebSocket
   * @param {Object|String} message - Message to send
   */
  send(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const messageString = typeof message === 'string' ? message : JSON.stringify(message)
      this.socket.send(messageString)
    } else {
      console.warn('WebSocket is not connected. Cannot send message:', message)
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    this.stopPing()
    
    if (this.socket) {
      this.socket.close(1000, 'Client disconnecting')
      this.socket = null
    }
    
    this.userId = null
    this.reconnectAttempts = 0
    this.isConnecting = false
  }

  /**
   * Add event listener
   * @param {string} event - Event type (open, message, close, error)
   * @param {function} callback - Callback function
   */
  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback)
    }
  }

  /**
   * Remove event listener
   * @param {string} event - Event type
   * @param {function} callback - Callback function to remove
   */
  off(event, callback) {
    if (this.listeners[event]) {
      const index = this.listeners[event].indexOf(callback)
      if (index > -1) {
        this.listeners[event].splice(index, 1)
      }
    }
  }

  /**
   * Emit event to all listeners
   * @param {string} event - Event type
   * @param {any} data - Event data
   */
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in WebSocket ${event} listener:`, error)
        }
      })
    }
  }

  /**
   * Get current connection state
   * @returns {string} Connection state
   */
  getState() {
    if (!this.socket) return 'CLOSED'
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING'
      case WebSocket.OPEN:
        return 'OPEN'
      case WebSocket.CLOSING:
        return 'CLOSING'
      case WebSocket.CLOSED:
        return 'CLOSED'
      default:
        return 'UNKNOWN'
    }
  }

  /**
   * Check if WebSocket is connected
   * @returns {boolean} True if connected
   */
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN
  }


}

// Create and export a single instance
const webSocketService = new WebSocketService()

export default webSocketService 


// WEBPACK FOOTER //
// ./src/services/WebSocketService.js